import React, { useState } from 'react';
import Posts from "../../posts/Posts";
import FeaturedArticle from '../Article/FeaturedArticle';
import { BodyContainer, Container } from "../StyledComponents/StyledComponents";
import styled from 'styled-components';

const ArticlesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
    
    @media (max-width: 1200px) {
        grid-template-columns: repeat(2, 1fr);
    }
    
    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 1rem;
    }
`;

const PaginationContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    margin-top: 3rem;
`;

const PageButton = styled.button`
    padding: 0.75rem 1rem;
    border: 2px solid ${props => props.theme.body.headers};
    background: ${props => props.active ? props.theme.body.headers : 'transparent'};
    color: ${props => props.active ? props.theme.body.background : props.theme.body.headers};
    border-radius: 4px;
    cursor: pointer;
    font-family: ${props => props.theme.typography.font};
    font-weight: 500;
    transition: all 0.3s ease;

    &:hover {
        background: ${props => props.theme.body.headers};
        color: ${props => props.theme.body.background};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        
        &:hover {
            background: transparent;
            color: ${props => props.theme.body.headers};
        }
    }
`;

const PageInfo = styled.span`
    color: ${props => props.theme.body.text};
    font-family: ${props => props.theme.typography.font};
`;

const FilterContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 2rem;
    justify-content: center;
    
    @media (max-width: 768px) {
        gap: 0.5rem;
        margin-bottom: 1.5rem;
    }
`;

const FilterButton = styled.button`
    padding: 0.5rem 1rem;
    border: 2px solid ${props => props.theme.body.headers};
    background: ${props => props.active ? props.theme.body.headers : 'transparent'};
    color: ${props => props.active ? props.theme.body.background : props.theme.body.headers};
    border-radius: 25px;
    cursor: pointer;
    font-family: ${props => props.theme.typography.font};
    font-weight: 500;
    font-size: 0.9rem;
    transition: all 0.3s ease;
    white-space: nowrap;

    &:hover {
        background: ${props => props.theme.body.headers};
        color: ${props => props.theme.body.background};
        transform: translateY(-2px);
    }
    
    @media (max-width: 768px) {
        padding: 0.4rem 0.8rem;
        font-size: 0.8rem;
    }
`;

const NoResultsMessage = styled.div`
    text-align: center;
    padding: 3rem 1rem;
    color: ${props => props.theme.body.text};
    font-family: ${props => props.theme.typography.font};
    font-size: 1.1rem;
    
    h3 {
        color: ${props => props.theme.body.headers};
        font-family: 'Playfair Display', serif;
        margin-bottom: 1rem;
        font-weight: 600;
    }
    
    p {
        opacity: 0.8;
        line-height: 1.6;
    }
`;

const ResultCount = styled.span`
    font-size: 0.9rem;
    color: ${props => props.theme.body.text};
    opacity: 0.7;
    font-weight: normal;
    margin-left: 0.5rem;
`;

const HomeProjects = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedCategory, setSelectedCategory] = useState('Todas');
    const postsPerPage = 9;

    // Lista de categorías únicas
    const categories = ['Todas', ...new Set(Posts.map(post => post.category))];

    // Calcular posts disponibles para "Más entradas" (excluyendo el featured que siempre es Posts[0])
    const availablePostsForGrid = selectedCategory === 'Todas' 
        ? Posts.slice(1) // Todos menos el featured
        : Posts.filter((post, index) => post.category === selectedCategory && index !== 0); // Solo la categoría seleccionada, excluyendo el featured si está en esa categoría
    
    // Para la primera página: Selección del editor + 9 entradas más
    // Para las siguientes páginas: 9 entradas regulares
    
    let currentPosts, totalPages;
    
    if (currentPage === 1) {
        // Primera página: hasta 9 posts de availablePostsForGrid
        currentPosts = availablePostsForGrid.slice(0, 9);
        // Total de páginas calculado basado en posts disponibles
        totalPages = Math.ceil(availablePostsForGrid.length / postsPerPage);
    } else {
        // Páginas siguientes
        const startIndex = (currentPage - 1) * postsPerPage;
        const endIndex = startIndex + postsPerPage;
        currentPosts = availablePostsForGrid.slice(startIndex, endIndex);
        totalPages = Math.ceil(availablePostsForGrid.length / postsPerPage);
    }
    
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        // Scroll hacia la sección de entradas
        const entradasSection = document.querySelector('[data-section="entradas"]');
        if (entradasSection) {
            entradasSection.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        } else {
            // Fallback si no encuentra la sección
            window.scrollTo({
                top: 400,
                behavior: 'smooth'
            });
        }
    };

    const handleCategoryChange = (category) => {
        setSelectedCategory(category);
        setCurrentPage(1); // Reset a la primera página al cambiar categoría
        // Removemos el scroll automático para que se quede donde está
    };

    return(
        <BodyContainer>
            <Container top={6}>
                {/* Artículo Destacado - solo en la primera página */}
                {currentPage === 1 && (
                    <>
                        <Container leftAlign bottom={4}>
                            <h2>Selección del editor</h2>
                        </Container>
                        
                        <FeaturedArticle
                            post={Posts[0]}
                            featured={true}
                        />
                        
                        {/* Más Entradas */}
                        <Container leftAlign bottom={2} top={6} data-section="entradas">
                            <h2>
                                {selectedCategory === 'Todas' ? 'Más entradas' : `Entradas de ${selectedCategory}`}
                                <ResultCount>
                                    ({availablePostsForGrid.length} {availablePostsForGrid.length !== 1 ? 'entradas' : 'entrada'})
                                </ResultCount>
                            </h2>
                        </Container>
                        
                        {/* Sistema de Filtros */}
                        <Container leftAlign bottom={4}>
                            <FilterContainer>
                                {categories.map((category, index) => (
                                    <FilterButton
                                        key={index}
                                        active={selectedCategory === category}
                                        onClick={() => handleCategoryChange(category)}
                                    >
                                        {category}
                                    </FilterButton>
                                ))}
                            </FilterContainer>
                        </Container>
                        
                        {currentPosts.length > 0 ? (
                            <ArticlesGrid>
                                {currentPosts.map((project, index) => {
                                    return (
                                        <FeaturedArticle
                                            key={index}
                                            post={project}
                                        />
                                    )})
                                }
                            </ArticlesGrid>
                        ) : (
                            <NoResultsMessage>
                                <h3>No hay entradas en esta categoría</h3>
                                <p>Intenta seleccionar otra categoría o vuelve a "Todas" para ver todas las entradas disponibles.</p>
                            </NoResultsMessage>
                        )}
                    </>
                )}
                
                {/* Páginas siguientes - solo artículos regulares */}
                {currentPage > 1 && (
                    <>
                        <Container leftAlign bottom={2} top={2} data-section="entradas">
                            <h2>
                                {selectedCategory === 'Todas' 
                                    ? `Entradas - Página ${currentPage}` 
                                    : `${selectedCategory} - Página ${currentPage}`
                                }
                                <ResultCount>
                                    ({availablePostsForGrid.length} total)
                                </ResultCount>
                            </h2>
                        </Container>
                        
                        {/* Sistema de Filtros para páginas siguientes */}
                        <Container leftAlign bottom={4}>
                            <FilterContainer>
                                {categories.map((category, index) => (
                                    <FilterButton
                                        key={index}
                                        active={selectedCategory === category}
                                        onClick={() => handleCategoryChange(category)}
                                    >
                                        {category}
                                    </FilterButton>
                                ))}
                            </FilterContainer>
                        </Container>
                        
                        {currentPosts.length > 0 ? (
                            <ArticlesGrid>
                                {currentPosts.map((project, index) => {
                                    return (
                                        <FeaturedArticle
                                            key={index}
                                            post={project}
                                        />
                                    )})
                                }
                            </ArticlesGrid>
                        ) : (
                            <NoResultsMessage>
                                <h3>No hay más entradas en esta categoría</h3>
                                <p>Intenta seleccionar otra categoría o vuelve a la primera página.</p>
                            </NoResultsMessage>
                        )}
                    </>
                )}
                
                {/* Controles de Paginación */}
                {totalPages > 1 && (
                    <PaginationContainer>
                        <PageButton 
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            ← Anterior
                        </PageButton>
                        
                        <PageInfo>
                            Página {currentPage} de {totalPages}
                        </PageInfo>
                        
                        <PageButton 
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            Siguiente →
                        </PageButton>
                    </PaginationContainer>
                )}
            </Container>
        </BodyContainer>
    )
}

export default HomeProjects;
